<script setup lang="ts">
/*
* Компонент SuggestionsRating - Рейтинг авторов предложений по улучшению (ППУ)
*
* Назначение:
* - Отображение статистики по авторам ППУ в виде таблицы
* - Поля: Инициатор (Аватар + ФИО + Должность), Отделение, Всего предложений, Принято (в виде зеленого чипа)
*/

import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import type { ISuggestionAuthorStat } from '@/modules/improvements/types/ISuggestionAuthorStat'
import { storeToRefs } from 'pinia'
import { onMounted } from 'vue'
import { useDisplay } from 'vuetify/framework'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoReload } from '@/modules/onboarding/composables/useOnboardingDemoReload'
import { IMPROVEMENTS_ANCHORS, IMPROVEMENTS_CONTEXTS } from '@/modules/onboarding/scenarios/improvements/anchors'

const { xs } = useDisplay()

useOnboardingContext({
  names: [IMPROVEMENTS_CONTEXTS.rating],
  root: () => document.body,
})
const improvementsStore = useImprovementsStore()
useOnboardingDemoReload('improvement', () => improvementsStore.fetchAuthorStats())
const { authorStats, isLoading } = storeToRefs(improvementsStore)

const headers = [
  { title: 'Автор', key: 'name', sortable: false },
  { title: 'Отделение', key: 'department', sortable: false },
  { title: 'Всего предложений', key: 'created_ppu_count', sortable: false },
  { title: 'Принято', key: 'accepted_ppu_count', sortable: false },
] as const

onMounted(() => {
  improvementsStore.fetchAuthorStats()
})

function getFullName(item: ISuggestionAuthorStat) {
  if (item.full_name) return item.full_name
  const parts = [item.last_name, item.first_name, item.middle_name].filter(Boolean)
  return parts.join(' ')
}

function getInitials(item: ISuggestionAuthorStat) {
  const f = item.first_name?.[0] || ''
  const l = item.last_name?.[0] || ''
  const res = (l + f).toUpperCase()
  return res || ''
}
</script>

<template>
  <div class="d-flex flex-column gap-4">
    <div class="d-flex justify-end">
      <OnboardingLaunchButton scenario="improvements.rating" />
    </div>
    <!-- Карточка таблицы рейтинга ППУ -->
    <VCard class="border overflow-hidden" elevation="0" :data-onboarding="IMPROVEMENTS_ANCHORS.rating.table">
      <VDataTable
        :headers="headers"
        :items="authorStats"
        :items-per-page="-1"
        :loading="isLoading"
        class="no-row-gaps cursor-pointer-table custom-data-table"
        hide-default-footer
      >
        <template #no-data>
          <div class="text-center text-italic py-4 text-medium-emphasis">
            Данные рейтинга отсутствуют
          </div>
        </template>

        <!-- Автор (Аватар + ФИО + Должность) -->
        <template #[`item.name`]="{ item }">
          <div class="d-flex align-center gap-3 py-2">
            <VAvatar
              v-if="item.avatar"
              :image="item.avatar"
              size="38"
            />
            <VAvatar
              v-else
              color="primary"
              variant="tonal"
              size="38"
            >
              <span class="text-caption font-weight-bold">
                {{ getInitials(item) }}
              </span>
            </VAvatar>
            <div class="d-flex flex-column">
              <span>{{ getFullName(item) }}</span>
              <span class="text-caption text-medium-emphasis">{{ item.position_name || '' }}</span>
            </div>
          </div>
        </template>

        <!-- Отделение -->
        <template #[`item.department`]="{ item }">
          <span>{{ item.department_name || '' }}</span>
        </template>

        <!-- Всего предложений -->
        <template #[`item.created_ppu_count`]="{ item }">
          <span>{{ item.created_ppu_count ?? 0 }}</span>
        </template>

        <!-- Принято (Чип цвета success) -->
        <template #[`item.accepted_ppu_count`]="{ item }">
          <VChip
            v-if="item.accepted_ppu_count > 0"
            color="success"
            variant="tonal"
            size="small"
            class="font-weight-medium px-3"
          >
            {{ item.accepted_ppu_count }}
          </VChip>
        </template>
      </VDataTable>
    </VCard>
  </div>
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}
</style>
